import { fetchComRetry } from './httpRetry';

const API_BASE = process.env.REACT_APP_AUTH_API_BASE || 'https://api.leveragesec.com.br';

export class EmissoesApiError extends Error {
  constructor(message, { status } = {}) {
    super(message);
    this.name = 'EmissoesApiError';
    this.status = status;
  }
}

async function _fetchEmissoes(comoConvidado) {
  let res;
  try {
    res = await fetchComRetry(`${API_BASE}/v1/emissoes/?detalhes=true`, {
      credentials: 'include',
      // Só tem efeito quando a sessão já é @leveragesec.com.br (checado no
      // backend) — pra qualquer outra sessão o header é ignorado, então não
      // precisa condicionar o envio por perfil aqui. Ver "interno com visão
      // de guest.txt" na raiz do repo.
      ...(comoConvidado ? { headers: { 'X-Visualizar-Como': 'convidado' } } : {}),
    });
  } catch (e) {
    throw new EmissoesApiError('Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.', { status: 0 });
  }
  if (res.status === 404) return [];
  if (!res.ok) throw new EmissoesApiError('Não foi possível carregar as emissões.', { status: res.status });
  const data = await res.json();
  return data.map(e => ({
    id: e.emissao_id,
    nome: e.emissao_nome,
    // Nome "de vitrine" (ex.: "15ª Emissão - CRI Casa Uvva"), calculado no
    // banco a partir do apelido definido em Adm > Publicar no Site. Por ora só
    // o seletor do cabeçalho (Detalhe2) e os cards de EmissoesEscolha usam;
    // exports/Painel/etc. seguem com `nome`.
    nomeExibicao: e.emissao_nome_exibicao || e.emissao_nome,
    apelido: e.emissao_apelido ?? null,
    numero: e.emissao_numero ?? null, // 015E_... -> 15; null fora do padrão
    tipo: e.emissao_tipo,
    codigoIf: Array.isArray(e.codigo_if) ? e.codigo_if : [],
    // null = desconhecida (emissão sem Akrual, ver possuiDadosAkrual). Só
    // informativo: o portal não filtra por `ativa` — visibilidade é a liberação
    // manual em /adm/liberacao-site.
    ativa: e.ativa === null ? null : e.ativa === true,
    // Projeto (ver services/projetos.js). Decisão sempre por projetoId;
    // projetoNome é só exibição.
    projetoId: e.projeto_id ?? 1,
    projetoNome: e.projeto_nome ?? null,
    // Visões (projetos) em que a emissão aparece — ver emissaoNoProjeto.
    projetosExibicao: Array.isArray(e.projetos_exibicao) ? e.projetos_exibicao : null,
    // false = emissão sem Akrual (projeto BTG): sem PU, situação, carteira e
    // integralização — as telas escondem esses blocos.
    possuiDadosAkrual: e.possui_dados_akrual !== false,
    // false = não pode ir pra carteira (projeto BTG, por enquanto) — sem
    // estrela; o backend também recusa em POST /auth/preferencias.
    favoritavel: e.favoritavel !== false,
    // Filtro "Companhia emissora" da visão BTG em EmissoesEscolha.
    companhiaEmissora: e.companhia_emissora ?? null,
    // Todas as séries dessa emissão visíveis ao perfil logado — a API já recorta
    // por permissão (client B2B ou usuário do portal), não filtra de novo aqui.
    // Sem distinção "publicada" (o conceito não existe mais no backend); quem
    // consome decide se filtra por `situacao` conforme o uso.
    series: Array.isArray(e.detalhes) ? e.detalhes.map(s => ({
      serieId: s.serie_id,
      serieNome: s.serie_nome,
      codigoIf: s.codigo_if,
      valorOferta: s.valor_oferta,
      situacao: s.situacao,
      dataEmissao: s.data_emissao,
      // cnpj_origem da EMISSÃO (igual em todas as séries dela): "Privada" | "88" | CNPJ | null.
      cnpjOrigem: s.cnpj_origem ?? null,
      // Série liberada no site (Control.DimSplitPortal, ver Adm/LiberacaoSite.jsx).
      // false só chega pra equipe Leverage — pros demais, série não liberada nem
      // vem na lista. Ausente (backend antigo) conta como liberada.
      liberadoSite: s.liberado_site !== false,
    })) : [],
  }));
}

// Promise-cache: chamadas simultâneas de múltiplos componentes compartilham a mesma
// requisição em voo. Em caso de erro, o cache é limpo para permitir nova tentativa.
let _emissoesPromise = null;

export function getEmissoes() {
  if (!_emissoesPromise) {
    _emissoesPromise = _fetchEmissoes(false).catch(e => { _emissoesPromise = null; throw e; });
  }
  return _emissoesPromise;
}

// Cache separada do getEmissoes() normal — recorte diferente (X-Visualizar-Como),
// não pode compartilhar promise com a chamada padrão. Só usada pelo toggle
// "visão pública" em EmissoesEscolha.jsx.
let _emissoesGuestPromise = null;

export function getEmissoesComoConvidado() {
  if (!_emissoesGuestPromise) {
    _emissoesGuestPromise = _fetchEmissoes(true).catch(e => { _emissoesGuestPromise = null; throw e; });
  }
  return _emissoesGuestPromise;
}

export function clearEmissoesCache() {
  _emissoesPromise = null;
  _emissoesGuestPromise = null;
}

// `comoConvidado` (default false) manda X-Visualizar-Como: convidado — mesmo
// header/regra de getEmissoesComoConvidado, só que aqui no POST de detalhe.
// Usado pelo toggle "visão pública" de Detalhe2.jsx.
export async function getEmissaoDetalhe(id, comoConvidado) {
  let res;
  try {
    res = await fetchComRetry(`${API_BASE}/v1/emissoes/`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(comoConvidado ? { 'X-Visualizar-Como': 'convidado' } : {}),
      },
      body: JSON.stringify({ emissao_ids: [id] }),
    });
  } catch (e) {
    throw new EmissoesApiError('Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.', { status: 0 });
  }
  if (!res.ok) throw new EmissoesApiError('Não foi possível carregar as características da emissão.', { status: res.status });
  const data = await res.json();
  if (!data.length) throw new EmissoesApiError('Emissão não encontrada.', { status: 404 });
  return data[0];
}

// Busca as características de várias emissões numa única chamada (a API já resolve
// isso em lote) — usado pela Carteira para evitar N requisições, uma por emissão.
export async function getEmissoesDetalhe(ids) {
  if (!ids || !ids.length) return [];
  let res;
  try {
    res = await fetchComRetry(`${API_BASE}/v1/emissoes/`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ emissao_ids: ids }),
    });
  } catch (e) {
    throw new EmissoesApiError('Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.', { status: 0 });
  }
  if (!res.ok) throw new EmissoesApiError('Não foi possível carregar as características das emissões.', { status: res.status });
  return res.json();
}
